import React from 'react';
import {
  Users,
  Phone,
  MessageCircle,
  Mail,
  Calendar,
  DollarSign,
  Target,
  TrendingUp,
  Linkedin
} from 'lucide-react';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import RecentLeadItem from './RecentLeadItem';
import LoadingScreen from '../shared/LoadingScreen';

const formatPipeline = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
};

const DashboardPage: React.FC = () => {
  const {
    metrics,
    leadGenStats,
    callAgentStats,
    whatsappAgentStats,
    emailAgentStats,
    linkedInAgentStats,
    recentLeads,
    isLoading,
  } = useDashboardStats();

  if (isLoading && metrics.qualifiedLeads === 0 && recentLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-8 pb-4">
      {/* Top 8 Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <Target size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.qualifiedLeads}</p>
              <p className="text-sm text-gray-700 font-semibold">Qualified Leads</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <Users size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.leadsContacted}</p>
              <p className="text-sm text-gray-700 font-semibold">Leads Contacted</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <Phone size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.callsDone}</p>
              <p className="text-sm text-gray-700 font-semibold">Calls Done</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <MessageCircle size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.whatsappsSent}</p>
              <p className="text-sm text-gray-700 font-semibold">WhatsApps Sent</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <Mail size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.emailsSent}</p>
              <p className="text-sm text-gray-700 font-semibold">Emails Sent</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <Linkedin size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.linkedInSent}</p>
              <p className="text-sm text-gray-700 font-semibold">LinkedIns Sent</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <Calendar size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">{metrics.meetingsBooked}</p>
              <p className="text-sm text-gray-700 font-semibold">Demos Booked</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 transition-all hover:shadow-2xl hover:border-[#05E0D1] shadow-lg hover:-translate-y-0.5 duration-300">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all">
              <DollarSign size={20} className="text-[#1875FF]" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">
                {formatPipeline(metrics.pipelineGenerated)}
              </p>
              <p className="text-sm text-gray-700 font-semibold">Pipeline Generated</p>
            </div>
          </div>
        </div>
      </div>

      {/* Two Big Boxes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Performance */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-[#1875FF]/20 p-7 shadow-2xl hover:shadow-3xl transition-all backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
                <TrendingUp className="text-white" size={20} />
              </div>
              <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Agent Performance</h3>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-5 bg-gradient-to-br from-[#1875FF]/10 to-[#1875FF]/20 rounded-2xl border-2 border-[#1875FF]/40 hover:shadow-xl transition-all hover:scale-[1.02] shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-md">
                  <Users className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-extrabold text-gray-900 text-lg font-display">Lead Gen Agent</h4>
                  <p className="text-sm text-gray-700 font-semibold">
                    {leadGenStats.preQualified} pre-qualified • {leadGenStats.qualified} qualified
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-[#1875FF] font-metric">{leadGenStats.conversionRate}%</div>
                <div className="text-xs text-gray-600 font-bold uppercase tracking-widest font-sans">conversion rate</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-5 bg-gradient-to-br from-[#05E0D1]/10 to-[#05E0D1]/20 rounded-2xl border-2 border-[#05E0D1]/40 hover:shadow-xl transition-all hover:scale-[1.02] shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center shadow-md">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-extrabold text-gray-900 text-lg font-display">Call Agent</h4>
                  <p className="text-sm text-gray-700 font-semibold">
                    {callAgentStats.totalCalls} calls • {callAgentStats.connected} connected • {callAgentStats.engaged} engaged • {callAgentStats.booked} booked
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-[#05E0D1] font-metric">{callAgentStats.conversionRate}%</div>
                <div className="text-xs text-gray-600 font-bold uppercase tracking-widest font-sans">conversion rate</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-5 bg-gradient-to-br from-[#52B5AE]/10 to-[#52B5AE]/20 rounded-2xl border-2 border-[#52B5AE]/40 hover:shadow-xl transition-all hover:scale-[1.02] shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#52B5AE] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-md">
                  <MessageCircle className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-extrabold text-gray-900 text-lg font-display">WhatsApp Agent</h4>
                  <p className="text-sm text-gray-700 font-semibold">
                    {whatsappAgentStats.totalSent} sent • {whatsappAgentStats.replies} replies • {whatsappAgentStats.engaged} engaged • {whatsappAgentStats.booked} booked
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-[#52B5AE] font-metric">{whatsappAgentStats.conversionRate}%</div>
                <div className="text-xs text-gray-600 font-bold uppercase tracking-widest font-sans">conversion rate</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-5 bg-gradient-to-br from-[#1875FF]/10 to-[#1875FF]/20 rounded-2xl border-2 border-[#1875FF]/40 hover:shadow-xl transition-all hover:scale-[1.02] shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-md">
                  <Mail className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-extrabold text-gray-900 text-lg font-display">Email Agent</h4>
                  <p className="text-sm text-gray-700 font-semibold">
                    {emailAgentStats.totalSent} sent • {emailAgentStats.replies} replies • {emailAgentStats.engaged} engaged • {emailAgentStats.booked} booked
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-[#1875FF] font-metric">{emailAgentStats.conversionRate}%</div>
                <div className="text-xs text-gray-600 font-bold uppercase tracking-widest font-sans">conversion rate</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-5 bg-gradient-to-br from-[#0A66C2]/10 to-[#0A66C2]/20 rounded-2xl border-2 border-[#0A66C2]/40 hover:shadow-xl transition-all hover:scale-[1.02] shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#0A66C2] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-md">
                  <Linkedin className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-extrabold text-gray-900 text-lg font-display">LinkedIn Agent</h4>
                  <p className="text-sm text-gray-700 font-semibold">
                    {linkedInAgentStats.totalSent} sent • {linkedInAgentStats.replies} replies • {linkedInAgentStats.engaged} engaged • {linkedInAgentStats.booked} booked
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-[#0A66C2] font-metric">{linkedInAgentStats.conversionRate}%</div>
                <div className="text-xs text-gray-600 font-bold uppercase tracking-widest font-sans">conversion rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recently Engaged Leads */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-[#05E0D1]/20 p-7 shadow-2xl hover:shadow-3xl transition-all backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#05E0D1] to-[#52B5AE] rounded-xl flex items-center justify-center shadow-lg">
                <Users className="text-white" size={20} />
              </div>
              <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Recently Engaged Leads</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recentLeads.length > 0 ? (
              recentLeads.map((lead, index) => (
                <RecentLeadItem key={lead.id || index} lead={lead} />
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-gray-500">
                <Users size={56} className="mx-auto mb-4 text-gray-300" />
                <p className="text-xl font-extrabold mb-2 text-gray-700 font-display">No Recent Engagement</p>
                <p className="text-sm font-semibold text-gray-500">Leads will appear here once they start engaging with your campaigns</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
